// Edit a customer's billing contact, billing email and phone (Session AQW-follow, Phase 2 item
// C-7). The server PATCH has accepted these three fields since the Customer model was built
// (bodySchema in customer.routes.ts); until this tab restructure there was simply no control for
// them anywhere in the office -- the "Contact" block was a bare read-only <dl>, unlike the
// secondary contact two sections below it, which already had a working inline Edit.
//
// A MODAL, NOT INLINE EDITING, DELIBERATELY. D27 (customer and site core-field editing) is an
// OPEN Director decision (docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md): whether this class of
// field should be editable inline at all, or only behind a confirm step, has not been ruled on.
// A modal with an explicit Save/Cancel is the conservative reading pending that ruling -- it is
// not offered here as the final answer to D27, only as what is safe to build before it.
//
// Follows EditTrainingSessionModal.tsx's convention: local field state is re-seeded from the
// record in a useEffect keyed on [open, record], so a cancelled edit never leaks into the next
// opening, and the parent owns the API call via onSave (mirroring every other inline edit already
// on this page -- name, secondary contact, communication preferences -- which all call
// customers.update() themselves rather than delegating the write into the modal).
import { useEffect, useState } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";

export interface CustomerContactFields {
  billingContact: string | null;
  billingEmail: string | null;
  phone: string | null;
}

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";

export function EditCustomerContactModal({
  open,
  record,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  record: CustomerContactFields;
  /** Shown inline when the last save attempt failed; the caller owns the message (and clearing it). */
  error?: string | null;
  onClose: () => void;
  /** Saves the change and reports whether it landed; the caller owns the error message either way. */
  onSave: (body: CustomerContactFields) => Promise<boolean>;
}) {
  const [billingContact, setBillingContact] = useState(record.billingContact ?? "");
  const [billingEmail, setBillingEmail] = useState(record.billingEmail ?? "");
  const [phone, setPhone] = useState(record.phone ?? "");
  const [saving, setSaving] = useState(false);

  // Re-seed every time the modal opens, so a cancelled edit never leaks into the next one and the
  // form always starts from what is actually on file.
  useEffect(() => {
    if (!open) return;
    setBillingContact(record.billingContact ?? "");
    setBillingEmail(record.billingEmail ?? "");
    setPhone(record.phone ?? "");
    setSaving(false);
  }, [open, record]);

  if (!open) return null;

  async function submit() {
    setSaving(true);
    const ok = await onSave({
      billingContact: billingContact.trim() || null,
      billingEmail: billingEmail.trim() || null,
      phone: phone.trim() || null,
    });
    if (ok) onClose();
    else setSaving(false);
  }

  return (
    <ModalShell ariaLabel="Edit contact details" onClose={() => !saving && onClose()} width="max-w-md">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Edit contact details</h2>
      <p className="mb-4 text-sm text-ele-body">The main billing contact for this customer: who ELE emails invoices and account correspondence to.</p>

      <div className="space-y-3">
        <div>
          <label className={labelCls} htmlFor="edit-contact-name">
            Contact name
          </label>
          <input id="edit-contact-name" value={billingContact} onChange={(e) => setBillingContact(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="edit-contact-email">
            Billing email
          </label>
          <input id="edit-contact-email" type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="edit-contact-phone">
            Phone
          </label>
          <input id="edit-contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <Button size="sm" onClick={() => void submit()} disabled={saving} className={MODAL_ACTION_BTN}>
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </ModalShell>
  );
}
